import type { ContentMasterFieldVersionView } from '../api/generated/models/ContentMasterFieldVersionView';
import type { ContentMasterLanguageVersionsView } from '../api/generated/models/ContentMasterLanguageVersionsView';
import type { ContentMasterProductView } from '../api/generated/models/ContentMasterProductView';
import { isLegacyMerkmalWertApiKey } from './contentMasterWawiExtendedFields';

/**
 * Per-language draft state derived from the cross-language `languageVersions`
 * map on `ContentMasterProductView`. Used to decorate the language switcher
 * with empty / unsaved-draft / fully-applied indicators (Content Master #180).
 *
 *   - `empty`   - no stored versions for this language at all;
 *   - `draft`   - at least one field's newest version has `isCurrent = false`,
 *                 i.e. there is unsaved work in this language;
 *   - `applied` - all fields' newest versions have `isCurrent = true` (every
 *                 stored field has been pushed to Wawi).
 *
 * Backend already sorts each `versions[apiName]` list by `updatedAt DESC`, so
 * `list[0]` is always the freshest and we do not re-sort here.
 */
export type ContentMasterLanguageState = 'empty' | 'draft' | 'applied';

const isLanguageEntryPopulated = (
  entry: ContentMasterLanguageVersionsView | undefined,
): entry is ContentMasterLanguageVersionsView & {
  versions: Record<string, Array<ContentMasterFieldVersionView>>;
} => {
  if (!entry || !entry.versions) {
    return false;
  }
  return Object.keys(entry.versions).length > 0;
};

/**
 * Multi-value Merkmal contract: every Merkmal is keyed by `wawiMerkmal_<X>`.
 * Deprecated `wawiMerkmalWert_<X>` keys may still appear in `versions` for
 * products saved before the migration; they describe individual `kMerkmalWert`
 * picks that the new keying scheme already covers under a single grouped
 * entry. Counting them here would over-report "has unsaved draft" because
 * they remain non-current until the product is re-saved with the new key.
 */
const isRenderableVersionKey = (key: string): boolean =>
  !isLegacyMerkmalWertApiKey(key);

export function getContentMasterLanguageState(
  product: ContentMasterProductView | null | undefined,
  lang: string,
): ContentMasterLanguageState {
  if (!product || !lang) {
    return 'empty';
  }
  const entry = product.languageVersions?.[lang];
  if (!isLanguageEntryPopulated(entry)) {
    return 'empty';
  }
  const lists = Object.entries(entry.versions)
    .filter(([key]) => isRenderableVersionKey(key))
    .map(([, list]) => list);
  // A field with an empty version list contributes neither "draft" nor
  // "applied" semantics - only fields that actually carry a newest version
  // matter. If every list happens to be empty we fall back to `empty`.
  const populatedLists = lists.filter(
    (list): list is Array<ContentMasterFieldVersionView> =>
      Array.isArray(list) && list.length > 0,
  );
  if (populatedLists.length === 0) {
    return 'empty';
  }
  const hasUnsaved = populatedLists.some((list) => list[0]?.isCurrent === false);
  return hasUnsaved ? 'draft' : 'applied';
}

/**
 * Pick which language to display when the product detail page opens.
 *
 * Order (mirrors the spec from CM #180):
 *   1. if the currently selected language is non-empty → keep it,
 *   2. else first language with an unsaved draft,
 *   3. else first language fully applied to Wawi,
 *   4. else workspace default language.
 *
 * `candidateLanguages` is the ordered list of active shop languages - order is
 * preserved when scanning for the first `draft` / `applied` match so the
 * selection is deterministic and matches the order the user sees in the
 * language switcher.
 */
export function pickContentMasterDefaultLanguage(
  product: ContentMasterProductView | null | undefined,
  candidateLanguages: string[],
  currentLang: string,
  workspaceDefaultLang: string,
): string {
  if (currentLang) {
    const currentState = getContentMasterLanguageState(product, currentLang);
    if (currentState !== 'empty') {
      return currentLang;
    }
  }

  const firstWithState = (target: ContentMasterLanguageState): string | undefined =>
    candidateLanguages.find(
      (lang) => getContentMasterLanguageState(product, lang) === target,
    );

  return firstWithState('draft') ?? firstWithState('applied') ?? workspaceDefaultLang;
}
